'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import DatePicker from 'react-datepicker';
import { zhCN, enUS } from 'date-fns/locale';
import { format } from 'date-fns';
import { generateRoomId } from '@/lib/client-utils';
import { useTranslation } from '@/lib/useTranslation';
import { LanguageSwitcher } from '@/lib/LanguageSwitcher';
import { brandConfig } from '@/lib/brand-config';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ScaleIn } from '@/components/animations';
import { Video, Calendar, LogIn, Users } from 'lucide-react';
import styles from '../styles/HomePage.module.css';
import inputStyles from '@/components/ui/Input.module.css';

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const tab = searchParams?.get('tab') || 'quick';
  const tabIndex = tab === 'schedule' ? 1 : tab === 'join' ? 2 : 0;

  const router = useRouter();
  function onTabSelected(index: number) {
    const tabName = index === 1 ? 'schedule' : index === 2 ? 'join' : 'quick';
    router.push(`/?tab=${tabName}`);
  }

  let tabs = React.Children.map(props.children, (child, index) => {
    return (
      <button
        className={styles.tab}
        onClick={() => {
          if (onTabSelected) {
            onTabSelected(index);
          }
        }}
        aria-pressed={tabIndex === index}
      >
        {/* @ts-ignore */}
        {child?.props.label}
      </button>
    );
  });

  return (
    <div>
      <div className={styles.tabs}>{tabs}</div>
      <div className={styles.tabContent}>
        {/* @ts-ignore */}
        {props.children[tabIndex]}
      </div>
    </div>
  );
}

// 快速会议 Tab
function QuickMeetingTab(_props: { label: string }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');

  const startMeeting = () => {
    if (!username.trim()) {
      alert(t('home.quickTab.usernamePlaceholder'));
      return;
    }
    const roomId = generateRoomId();
    router.push(`/rooms/${roomId}?username=${encodeURIComponent(username)}&autoJoin=true`);
  };

  return (
    <ScaleIn>
      <Card variant="glass" padding="lg">
        <CardHeader>
          <CardTitle>{t('home.tabs.quick')}</CardTitle>
          <CardDescription>{t('home.quickTab.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={styles.formGroup}>
          <Input
            label={t('home.quickTab.usernamePlaceholder')}
            placeholder={t('home.quickTab.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            leftIcon={<Users size={20} />}
          />
          <Input
            label={t('home.quickTab.meetingTitlePlaceholder')}
            placeholder={t('home.quickTab.meetingTitlePlaceholder')}
            value={meetingTitle}
            onChange={(e) => setMeetingTitle(e.target.value)}
            fullWidth
            leftIcon={<Video size={20} />}
          />
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={startMeeting}
            icon={<Video size={20} />}
          >
            {t('home.quickTab.startMeeting')}
          </Button>
        </div>
      </CardContent>
    </Card>
    </ScaleIn>
  );
}

// 预约会议 Tab
function ScheduleMeetingTab(_props: { label: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t, locale } = useTranslation();

  // 从 URL 读取 username 参数
  const urlUsername = searchParams?.get('username') || '';
  const [username, setUsername] = useState(urlUsername);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);


  // React DatePicker 自定义输入（使用现有 Input 组件）
  const ScheduleDateInput = React.useMemo(() => {
    const Component = React.forwardRef<HTMLInputElement, any>(({ value, onClick }, ref) => (
      <Input
        placeholder={locale === 'zh-CN' ? 'yyyy/MM/dd HH:mm' : 'MM/dd/yyyy h:mm a'}
        value={value}
        onClick={onClick}
        readOnly
        fullWidth
        leftIcon={<Calendar size={20} />}
        ref={ref as any}
      />
    ));
    Component.displayName = 'ScheduleDateInput';
    return Component;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, locale]);

  // 国际化 DatePicker 时间标签
  React.useEffect(() => {
    const timeLabel = locale === 'zh-CN' ? '时间' : 'Time';

    // 更新现有的时间标签
    const updateTimeLabel = () => {
      const timeHeader = document.querySelector('.react-datepicker-time__header');
      if (timeHeader && timeHeader.textContent !== timeLabel) {
        timeHeader.textContent = timeLabel;
      }
    };

    // 使用 requestAnimationFrame 确保 DOM 已更新
    const rafId = requestAnimationFrame(() => {
      updateTimeLabel();
    });

    // 监听 DOM 变化，当 DatePicker 打开时更新
    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateTimeLabel);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [locale]);

  // 当 URL 中的 username 改变时，更新状态
  React.useEffect(() => {
    if (urlUsername && urlUsername !== username) {
      setUsername(urlUsername);
    }
  }, [urlUsername]);

  // 加载会议列表
  const loadMeetings = async () => {
    if (!username.trim()) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/meetings/list?organizer=${encodeURIComponent(username)}`);
      const data = await response.json();
      if (data.success) {
        setMeetings(data.meetings);
      }
    } catch (error) {
      console.error('Failed to load meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  // 创建会议
  const createMeeting = async () => {
    if (!username.trim() || !meetingTitle.trim()) {
      alert('请填写用户名和会议主题');
      return;
    }

    try {
      setLoading(true);
      const roomId = generateRoomId();
      const response = await fetch('/api/meetings/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: meetingTitle,
          organizer: username,
          room_name: roomId,
          scheduled_time: scheduledTime ? new Date(scheduledTime).getTime() : null,
          duration: parseInt(duration),
        }),
      });

      const data = await response.json();
      if (data.success) {
        alert('会议创建成功！');
        setMeetingTitle('');
        setScheduledTime('');
        setDuration('60');
        loadMeetings();
      } else {
        alert('创建失败：' + data.error);
      }
    } catch (error) {
      console.error('Failed to create meeting:', error);
      alert('创建失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 加入会议
  const joinMeeting = (roomName: string) => {
    router.push(`/rooms/${roomName}?username=${encodeURIComponent(username)}&autoJoin=true`);
  };

  // 删除会议
  const deleteMeeting = async (id: number) => {
    if (!confirm(t('home.scheduleTab.confirmDelete'))) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/meetings/delete?id=${id}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        alert('会议已删除');
        loadMeetings();
      } else {
        alert('删除失败：' + data.error);
      }
    } catch (error) {
      console.error('Failed to delete meeting:', error);
      alert('删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 生成会议邀请文案
  const generateInvitation = (meeting: any) => {
    const baseUrl = window.location.origin;
    const meetingUrl = `${baseUrl}/rooms/${meeting.room_name}`;

    let timeStr = '';
    if (meeting.scheduled_time) {
      const startTime = new Date(meeting.scheduled_time);
      const endTime = new Date(meeting.scheduled_time + (meeting.duration || 60) * 60 * 1000);
      const timeLabel = locale === 'zh-CN' ? '会议时间：' : 'Time: ';
      timeStr = `${timeLabel}${startTime.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })}-${endTime.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    }

    return `${meeting.organizer} 邀请您参加会议
会议主题：${meeting.title}
${timeStr ? timeStr + '\n' : ''}
点击链接入会：
${meetingUrl}

#会议：${meeting.room_name}`;
  };

  // 复制会议邀请
  const shareMeeting = async (meeting: any) => {
    const invitation = generateInvitation(meeting);
    try {
      await navigator.clipboard.writeText(invitation);
      alert('会议邀请已复制到剪贴板');
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('复制失败，请手动复制');
    }
  };

  // 当用户名改变时加载会议列表
  React.useEffect(() => {
    if (username.trim()) {
      loadMeetings();
    } else {
      setMeetings([]);
    }
  }, [username]);

  return (
    <ScaleIn>
      <Card variant="glass" padding="lg">
        <CardHeader>
          <CardTitle>{t('home.tabs.schedule')}</CardTitle>
          <CardDescription>预约一个会议,生成会议邀请供他人加入。</CardDescription>
        </CardHeader>
      <CardContent>
        <div className={styles.formGroup}>
          <Input
            label={t('home.scheduleTab.usernamePlaceholder')}
            placeholder={t('home.scheduleTab.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            leftIcon={<Users size={20} />}
          />
          <Input
            label={t('home.scheduleTab.meetingTitlePlaceholder')}
            placeholder={t('home.scheduleTab.meetingTitlePlaceholder')}
            value={meetingTitle}
            onChange={(e) => setMeetingTitle(e.target.value)}
            fullWidth
            leftIcon={<Calendar size={20} />}
          />
          <div className={inputStyles.wrapper}>
            <label className={inputStyles.label}>{t('home.scheduleTab.scheduledTime') || '预约时间'}</label>
            <DatePicker
              selected={scheduledTime ? new Date(scheduledTime) : null}
              onChange={(d: Date | null) => setScheduledTime(d ? format(d, "yyyy-MM-dd'T'HH:mm") : '')}
              showTimeSelect
              timeIntervals={5}
              dateFormat={locale === 'zh-CN' ? 'yyyy/MM/dd HH:mm' : 'MM/dd/yyyy h:mm aa'}
              timeFormat={locale === 'zh-CN' ? 'HH:mm' : 'h:mm aa'}
              locale={locale === 'zh-CN' ? zhCN : enUS}
              todayButton={locale === 'zh-CN' ? '今天' : 'Today'}
              isClearable
              placeholderText={locale === 'zh-CN' ? 'yyyy/MM/dd HH:mm' : 'MM/dd/yyyy h:mm a'}
              customInput={React.createElement(ScheduleDateInput)}
            />
          </div>
          <Input
            type="number"
            label={t('home.scheduleTab.duration')}
            placeholder={t('home.scheduleTab.duration')}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            fullWidth
            min="15"
            step="15"
          />
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={createMeeting}
            disabled={loading}
            loading={loading}
            icon={<Calendar size={20} />}
          >
            {t('home.scheduleTab.createMeeting')}
          </Button>
        </div>

        {/* 会议列表 */}
        {username.trim() && (
          <div style={{ marginTop: 'var(--spacing-2xl)' }}>
            <h3 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-semibold)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              {t('home.scheduleTab.myMeetings')}
            </h3>
            {loading ? (
              <p>{t('common.loading')}</p>
            ) : meetings.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>{t('home.scheduleTab.noMeetings')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                {meetings.map((meeting) => (
                  <Card key={meeting.id} variant="default" padding="md" hoverable>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-lg)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-lg)', marginBottom: 'var(--spacing-xs)' }}>
                          {meeting.title}
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--spacing-xs)' }}>
                          {meeting.scheduled_time
                            ? new Date(meeting.scheduled_time).toLocaleString(locale)
                            : t('home.tabs.quick')}
                        </div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                          ID: {meeting.room_name}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => shareMeeting(meeting)}
                          title="复制会议邀请"
                        >
                          {t('home.scheduleTab.share')}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => joinMeeting(meeting.room_name)}
                        >
                          {t('home.scheduleTab.join')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => deleteMeeting(meeting.id)}
                        >
                          {t('home.scheduleTab.delete')}
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
    </ScaleIn>
  );
}

// 加入会议 Tab
function JoinMeetingTab(_props: { label: string }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [roomId, setRoomId] = useState('');
  const [username, setUsername] = useState('');

  const joinMeeting = () => {
    if (!roomId.trim() || !username.trim()) {
      alert('请填写会议 ID 和用户名');
      return;
    }
    router.push(`/rooms/${roomId}?username=${encodeURIComponent(username)}&autoJoin=true`);
  };

  return (
    <ScaleIn>
      <Card variant="glass" padding="lg">
        <CardHeader>
          <CardTitle>{t('home.tabs.join')}</CardTitle>
          <CardDescription>{t('home.joinTab.description')}</CardDescription>
        </CardHeader>
      <CardContent>
        <div className={styles.formGroup}>
          <Input
            label={t('home.joinTab.roomIdPlaceholder')}
            placeholder={t('home.joinTab.roomIdPlaceholder')}
            value={roomId}
            onChange={(e) => setRoomId(e.target.value)}
            fullWidth
            leftIcon={<Video size={20} />}
          />
          <Input
            label={t('home.joinTab.usernamePlaceholder')}
            placeholder={t('home.joinTab.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            leftIcon={<Users size={20} />}
          />
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={joinMeeting}
            icon={<LogIn size={20} />}
          >
            {t('home.joinTab.joinMeeting')}
          </Button>
        </div>
      </CardContent>
    </Card>
    </ScaleIn>
  );
}

export default function Page() {
  const { t, locale } = useTranslation();
  const isZh = locale === 'zh-CN';

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            <img
              src={isZh ? '/images/logo.svg' : '/images/logo-en.svg'}
              alt={brandConfig.company.name}
              className={styles.logoImage}
            />
          </div>
          <div className={styles.headerActions}>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        <div className={styles.mainContent}>
          <Suspense fallback={<div>{t('common.loading')}</div>}>
            <Tabs>
              <QuickMeetingTab label={t('home.tabs.quick')} />
              <ScheduleMeetingTab label={t('home.tabs.schedule')} />
              <JoinMeetingTab label={t('home.tabs.join')} />
            </Tabs>
          </Suspense>
        </div>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <p style={{ color: 'var(--text-tertiary)', margin: 0, textAlign: 'center' }}>
            {brandConfig.legal.copyright}
          </p>
        </div>
      </footer>
    </div>
  );
}
